import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router';

import type { ContactCallScope } from '@/lib/api/calls';

export interface ContactDrawerHint {
  name?: string | null;
  scope?: ContactCallScope;
}

interface ContactDrawerValue {
  phone: string | null;
  hint: ContactDrawerHint | null;
  open: (phone: string, hint?: ContactDrawerHint) => void;
  close: () => void;
}

const ContactDrawerContext = createContext<ContactDrawerValue | null>(null);

/** History state key for an open contact panel. */
const HISTORY_KEY = 'contactPanel';
type HistoryEntry = { phone: string; hint: ContactDrawerHint | null };

function historyEntry(state: unknown): HistoryEntry | undefined {
  if (!state || typeof state !== 'object') return undefined;
  const entry = (state as Record<string, unknown>)[HISTORY_KEY];
  return entry && typeof entry === 'object' && typeof (entry as HistoryEntry).phone === 'string'
    ? (entry as HistoryEntry)
    : undefined;
}

/**
 * The open panel is a history entry on the current page, so the phone's Back
 * button (or the browser's) closes the panel instead of changing the page
 * underneath it. Leaving the page closes the panel; coming back to that entry
 * opens it again.
 */
export function ContactDrawerProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState<{ phone: string | null; hint: ContactDrawerHint | null }>({
    phone: null,
    hint: null,
  });
  const latest = useRef(location);
  latest.current = location;
  // True while the panel owns the current history entry.
  const pushed = useRef(false);
  // Set by close() until the history step back has landed.
  const closing = useRef(false);

  useEffect(() => {
    const entry = historyEntry(location.state);
    if (entry) {
      if (closing.current) return;
      pushed.current = true;
      setState((s) => (s.phone === entry.phone ? s : { phone: entry.phone, hint: entry.hint }));
      return;
    }
    closing.current = false;
    pushed.current = false;
    setState((s) => (s.phone ? { ...s, phone: null } : s));
  }, [location.key, location.state]);

  const open = useCallback(
    (phone: string, hint?: ContactDrawerHint) => {
      if (!phone) return;
      const next = { phone, hint: hint ?? null };
      setState(next);
      closing.current = false;
      const here = latest.current;
      const base =
        here.state && typeof here.state === 'object' ? (here.state as Record<string, unknown>) : {};
      navigate(
        { pathname: here.pathname, search: here.search, hash: here.hash },
        {
          state: { ...base, [HISTORY_KEY]: next },
          // Switching contacts inside an open panel keeps a single Back step.
          replace: pushed.current,
          preventScrollReset: true,
        },
      );
      pushed.current = true;
    },
    [navigate],
  );

  const close = useCallback(() => {
    setState((s) => ({ ...s, phone: null }));
    if (pushed.current && historyEntry(latest.current.state)) {
      pushed.current = false;
      closing.current = true;
      navigate(-1);
    }
  }, [navigate]);

  const value = useMemo(() => ({ ...state, open, close }), [state, open, close]);
  return <ContactDrawerContext.Provider value={value}>{children}</ContactDrawerContext.Provider>;
}

export function useContactDrawer() {
  const ctx = useContext(ContactDrawerContext);
  if (!ctx) throw new Error('useContactDrawer must be used inside ContactDrawerProvider');
  return ctx;
}
